import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Row, Col, Table, Spinner } from 'react-bootstrap';
import { parivartanAPI } from '../../services/parivartanAPI';
import { TENDER_APP_NAME } from '../../config/branding';
import { applyDhruvDefaultsIfEmpty, mergeOrganisationFromCompany } from '../../config/dhruvCorporateDefaults';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useAltF1TestFill } from '../../hooks/useAltF1TestFill';
import {
  dummyParivartanDepartmentForm,
  dummyParivartanLocationForm
} from '../../utils/testFormDummies';
import './CompanyOnboardingPage.scss';

const STEPS = [
  'Organisation identity',
  'Locations & departments',
  'Platform owner',
  'Regional defaults',
  'Applications',
  'Review & activate'
];

const LEGAL_ENTITY_OPTIONS = [
  { value: 'PUBLIC_LIMITED', label: 'Public limited company' },
  { value: 'PRIVATE_LIMITED', label: 'Private limited company' },
  { value: 'LLP', label: 'Limited liability partnership' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'SOLE_PROPRIETORSHIP', label: 'Sole proprietorship' },
  { value: 'OTHER', label: 'Other' }
];

const COMPANY_SIZE_OPTIONS = [
  { value: 'STARTUP', label: 'Startup' },
  { value: 'SMALL', label: 'Small' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LARGE', label: 'Large' },
  { value: 'ENTERPRISE', label: 'Enterprise' }
];

const emptyLocation = () => ({ name: '', code: '', isHeadOffice: false });
const emptyDepartment = () => ({ name: '', code: '' });

const CompanyOnboardingPage = () => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(null);
  const [org, setOrg] = useState(() => mergeOrganisationFromCompany({}));
  const [regional, setRegional] = useState({ currency: 'INR', timezone: 'Asia/Kolkata', financialYearStartMonth: 4 });
  const [locForm, setLocForm] = useState(emptyLocation());
  const [deptForm, setDeptForm] = useState(emptyDepartment());
  const [ownerUserId, setOwnerUserId] = useState('');
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [tenderEnabled, setTenderEnabled] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useDocumentTitle('Company setup');

  const refresh = () => parivartanAPI.getOnboarding().then((r) => {
    const d = r.data?.data;
    setData(d);
    setStep(d?.draft?.currentStep || 1);
    const merged = applyDhruvDefaultsIfEmpty(
      mergeOrganisationFromCompany(d?.company, d?.draft?.organisation),
      d?.company?.code
    );
    setOrg(merged);
    if (d?.draft?.ownerUserId) setOwnerUserId(d.draft.ownerUserId);
    if (d?.draft?.regionalDefaults) setRegional({ ...regional, ...d.draft.regionalDefaults });
    const tenderEnt = (d?.company?.applicationEntitlements || []).find((e) => e.applicationKey === 'tender');
    if (tenderEnt) setTenderEnabled(Boolean(tenderEnt.enabled));
  });

  useEffect(() => {
    refresh().catch(() => setError('Unable to load onboarding'));
    parivartanAPI.listUsers({ active: 'true', limit: 100 }).then((r) => setEligibleUsers(r.data?.data?.users || []));
  }, []);

  useAltF1TestFill(step === 2, () => {
    setLocForm(dummyParivartanLocationForm());
    setDeptForm(dummyParivartanDepartmentForm());
  });

  const updateContact = (patch) => setOrg((prev) => ({ ...prev, contact: { ...prev.contact, ...patch } }));
  const updateAddress = (patch) => setOrg((prev) => ({
    ...prev,
    contact: { ...prev.contact, address: { ...prev.contact.address, ...patch } }
  }));
  const updateCorporate = (patch) => setOrg((prev) => ({
    ...prev,
    corporateProfile: { ...prev.corporateProfile, ...patch }
  }));
  const updateSecEmails = (patch) => setOrg((prev) => ({
    ...prev,
    corporateProfile: {
      ...prev.corporateProfile,
      secondaryEmails: { ...prev.corporateProfile.secondaryEmails, ...patch }
    }
  }));
  const updateCompliance = (patch) => setOrg((prev) => ({
    ...prev,
    metadata: {
      ...prev.metadata,
      compliance: { ...prev.metadata.compliance, ...patch }
    }
  }));

  const restorePublicReference = () => {
    setOrg(applyDhruvDefaultsIfEmpty(mergeOrganisationFromCompany({}), 'DCSL'));
  };

  const validateStep = () => {
    const errs = {};
    if (step === 1) {
      if (!org.legalName?.trim()) errs.legalName = 'Legal name is required';
      if (!org.code?.trim()) errs.code = 'Company code is required';
      if (!org.displayName?.trim()) errs.displayName = 'Display name is required';
      if (!org.contact?.email?.trim()) errs.contactEmail = 'Primary email is required';
      if (!org.corporateProfile?.cin?.trim()) errs.cin = 'CIN is required for a listed company';
    }
    if (step === 2) {
      if (!(data?.locations || []).length) errs.locations = 'Add at least one location';
    }
    if (step === 3 && !ownerUserId) errs.owner = 'Select a platform owner';
    setFieldErrors(errs);
    return !Object.keys(errs).length;
  };

  const saveLocation = async () => {
    setSaving(true);
    setError('');
    try {
      await parivartanAPI.upsertLocation({ ...locForm, isActive: true });
      setLocForm(emptyLocation());
      await refresh();
    } catch (e) {
      setError(e.response?.data?.message || 'Location save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveDepartment = async () => {
    setSaving(true);
    try {
      await parivartanAPI.upsertDepartment({ ...deptForm, isActive: true });
      setDeptForm(emptyDepartment());
      await refresh();
    } catch (e) {
      setError(e.response?.data?.message || 'Department save failed');
    } finally {
      setSaving(false);
    }
  };

  const save = async (nextStep) => {
    if (!validateStep()) return;
    setSaving(true);
    setError('');
    try {
      let payload = {};
      if (step === 1) payload = { organisation: org };
      if (step === 3) payload = { ownerUserId };
      if (step === 4) payload = { regionalDefaults: regional };
      if (step === 5) payload = { applicationActivation: [{ applicationKey: 'tender', enabled: tenderEnabled }] };
      await parivartanAPI.saveOnboardingStep(step, payload);
      setStep(nextStep);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const activate = async () => {
    setSaving(true);
    setError('');
    try {
      await parivartanAPI.activateOnboarding();
      await refresh();
    } catch (e) {
      setError(e.response?.data?.message || 'Activation failed');
    } finally {
      setSaving(false);
    }
  };

  if (!data && !error) return <Spinner size="sm" animation="border" />;

  const locations = data?.locations || [];
  const departments = data?.departments || [];
  const cp = org.corporateProfile || {};
  const isDcsl = String(org.code || data?.company?.code || '').toUpperCase() === 'DCSL';

  const renderStep1 = () => (
    <>
      {isDcsl ? (
        <div className="company-onboarding__source-note">
          Reference fields for <strong>Dhruv Consultancy Services Ltd</strong> are prefilled from public sources
          (BSE/NSE listings, MCA CIN, company website). Review and adjust before saving.
          <div className="mt-2">
            <Button type="button" size="sm" variant="outline-secondary" onClick={restorePublicReference}>
              Restore public reference data
            </Button>
          </div>
        </div>
      ) : null}

      <section className="company-onboarding__card">
        <h3 className="company-onboarding__card-title">Legal identity</h3>
        <p className="company-onboarding__card-hint">How your organisation appears on tenders, letters, and compliance outputs.</p>
        <Row className="g-3">
          <Col md={12}>
            <Form.Group>
              <Form.Label>Legal name</Form.Label>
              <Form.Control
                isInvalid={!!fieldErrors.legalName}
                value={org.legalName}
                onChange={(e) => setOrg({ ...org, legalName: e.target.value })}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.legalName}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Display / trading name</Form.Label>
              <Form.Control
                isInvalid={!!fieldErrors.displayName}
                value={org.displayName}
                onChange={(e) => setOrg({ ...org, displayName: e.target.value })}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.displayName}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Company code</Form.Label>
              <Form.Control
                isInvalid={!!fieldErrors.code}
                value={org.code}
                onChange={(e) => setOrg({ ...org, code: e.target.value.toUpperCase() })}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.code}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Organisation size</Form.Label>
              <Form.Select value={org.companySize} onChange={(e) => setOrg({ ...org, companySize: e.target.value })}>
                {COMPANY_SIZE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Industry</Form.Label>
              <Form.Control value={org.industry} onChange={(e) => setOrg({ ...org, industry: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Legal entity type</Form.Label>
              <Form.Select
                value={org.metadata?.legalEntityType || 'PRIVATE_LIMITED'}
                onChange={(e) => setOrg({
                  ...org,
                  metadata: { ...org.metadata, legalEntityType: e.target.value }
                })}
              >
                {LEGAL_ENTITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group>
              <Form.Label>Company profile (short)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={org.description}
                onChange={(e) => setOrg({ ...org, description: e.target.value })}
              />
            </Form.Group>
          </Col>
        </Row>
      </section>

      <section className="company-onboarding__card">
        <h3 className="company-onboarding__card-title">Statutory &amp; exchange registration</h3>
        <p className="company-onboarding__card-hint">Corporate Identification Number, listing, and registrar details for a public limited company.</p>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>CIN</Form.Label>
              <Form.Control
                isInvalid={!!fieldErrors.cin}
                value={cp.cin || ''}
                onChange={(e) => updateCorporate({ cin: e.target.value })}
                placeholder="L74999MH2003PLC141887"
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.cin}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Date of incorporation</Form.Label>
              <Form.Control
                type="date"
                value={cp.incorporationDate || ''}
                onChange={(e) => updateCorporate({ incorporationDate: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>RoC jurisdiction</Form.Label>
              <Form.Control
                value={cp.rocJurisdiction || ''}
                onChange={(e) => updateCorporate({ rocJurisdiction: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>LEI</Form.Label>
              <Form.Control value={cp.lei || ''} onChange={(e) => updateCorporate({ lei: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>ISIN</Form.Label>
              <Form.Control value={cp.isin || ''} onChange={(e) => updateCorporate({ isin: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>ISO certification</Form.Label>
              <Form.Control
                value={cp.isoCertification || ''}
                onChange={(e) => updateCorporate({ isoCertification: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>BSE security code</Form.Label>
              <Form.Control
                value={cp.bseSecurityCode || ''}
                onChange={(e) => updateCorporate({ bseSecurityCode: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>BSE security ID</Form.Label>
              <Form.Control
                value={cp.bseSecurityId || ''}
                onChange={(e) => updateCorporate({ bseSecurityId: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>NSE symbol</Form.Label>
              <Form.Control value={cp.nseSymbol || ''} onChange={(e) => updateCorporate({ nseSymbol: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Face value (₹)</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={cp.faceValueInr ?? ''}
                onChange={(e) => updateCorporate({ faceValueInr: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Check
              type="checkbox"
              id="listed-bse"
              label="Listed on BSE (main board)"
              checked={Boolean(cp.listedOnBse)}
              onChange={(e) => updateCorporate({ listedOnBse: e.target.checked })}
              className="mb-2"
            />
            <Form.Check
              type="checkbox"
              id="listed-nse"
              label="Listed on NSE"
              checked={Boolean(cp.listedOnNse)}
              onChange={(e) => updateCorporate({ listedOnNse: e.target.checked })}
            />
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Registrar &amp; transfer agent</Form.Label>
              <Form.Control
                value={cp.registrarName || ''}
                onChange={(e) => updateCorporate({ registrarName: e.target.value })}
              />
            </Form.Group>
          </Col>
        </Row>
      </section>

      <section className="company-onboarding__card">
        <h3 className="company-onboarding__card-title">Registered office</h3>
        <p className="company-onboarding__card-hint">Official address as per MCA / exchange filings.</p>
        <Row className="g-3">
          <Col md={12}>
            <Form.Group>
              <Form.Label>Address line</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={org.contact?.address?.street || ''}
                onChange={(e) => updateAddress({ street: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>City</Form.Label>
              <Form.Control
                value={org.contact?.address?.city || ''}
                onChange={(e) => updateAddress({ city: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>State</Form.Label>
              <Form.Control
                value={org.contact?.address?.state || ''}
                onChange={(e) => updateAddress({ state: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>PIN</Form.Label>
              <Form.Control
                value={org.contact?.address?.postalCode || ''}
                onChange={(e) => updateAddress({ postalCode: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Country</Form.Label>
              <Form.Control
                value={org.contact?.address?.country || 'India'}
                onChange={(e) => updateAddress({ country: e.target.value })}
              />
            </Form.Group>
          </Col>
        </Row>
      </section>

      <section className="company-onboarding__card">
        <h3 className="company-onboarding__card-title">Contact &amp; tax identifiers</h3>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Primary email</Form.Label>
              <Form.Control
                type="email"
                isInvalid={!!fieldErrors.contactEmail}
                value={org.contact?.email || ''}
                onChange={(e) => updateContact({ email: e.target.value })}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.contactEmail}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>General / services email</Form.Label>
              <Form.Control
                type="email"
                value={cp.secondaryEmails?.services || ''}
                onChange={(e) => updateSecEmails({ services: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Company secretary email</Form.Label>
              <Form.Control
                type="email"
                value={cp.secondaryEmails?.companySecretary || ''}
                onChange={(e) => updateSecEmails({ companySecretary: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Investor relations email</Form.Label>
              <Form.Control
                type="email"
                value={cp.secondaryEmails?.investorRelations || ''}
                onChange={(e) => updateSecEmails({ investorRelations: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Telephone</Form.Label>
              <Form.Control value={org.contact?.phone || ''} onChange={(e) => updateContact({ phone: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Website</Form.Label>
              <Form.Control value={org.contact?.website || ''} onChange={(e) => updateContact({ website: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>PAN</Form.Label>
              <Form.Control
                value={org.metadata?.compliance?.panNumber || ''}
                onChange={(e) => updateCompliance({ panNumber: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>GSTIN</Form.Label>
              <Form.Control
                value={org.metadata?.compliance?.gstNumber || ''}
                onChange={(e) => updateCompliance({ gstNumber: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Check
              type="switch"
              id="gst-registered"
              label="GST registered"
              checked={Boolean(org.metadata?.compliance?.gstRegistered)}
              onChange={(e) => updateCompliance({ gstRegistered: e.target.checked })}
            />
          </Col>
        </Row>
      </section>
    </>
  );

  return (
    <div className="company-onboarding">
      <header className="company-onboarding__header">
        <h1 className="company-onboarding__title">Company onboarding</h1>
        <p className="company-onboarding__subtitle">
          Capture your organisation&apos;s legal, statutory, and contact profile once — we reuse it across
          {` ${TENDER_APP_NAME}`}, documents, and workspace settings.
        </p>
      </header>

      <div className="company-onboarding__stepper" aria-label="Onboarding steps">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const cls = [
            'company-onboarding__step-pill',
            n === step ? 'company-onboarding__step-pill--active' : '',
            n < step ? 'company-onboarding__step-pill--done' : ''
          ].filter(Boolean).join(' ');
          return <span key={label} className={cls}>{n}. {label}</span>;
        })}
      </div>

      <div className="company-onboarding__progress" aria-hidden>
        <div className="company-onboarding__progress-bar" style={{ width: `${(step / STEPS.length) * 100}%` }} />
      </div>

      <div className="company-onboarding__step-heading">Step {step} of {STEPS.length}</div>
      <div className="company-onboarding__step-name">{STEPS[step - 1]}</div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {step === 1 && renderStep1()}

      {step === 2 && (
        <div className="company-onboarding__card">
          {fieldErrors.locations ? <Alert variant="warning">{fieldErrors.locations}</Alert> : null}
          <h3 className="company-onboarding__card-title">Locations</h3>
          <Table size="sm" className="mb-3"><thead><tr><th>Name</th><th>Code</th><th>HQ</th></tr></thead>
            <tbody>{locations.map((l) => <tr key={l._id}><td>{l.name}</td><td>{l.code}</td><td>{l.isHeadOffice ? 'Yes' : ''}</td></tr>)}</tbody>
          </Table>
          <Row className="g-2 mb-4">
            <Col md={4}><Form.Control placeholder="Name" value={locForm.name} onChange={(e) => setLocForm({ ...locForm, name: e.target.value })} /></Col>
            <Col md={3}><Form.Control placeholder="Code" value={locForm.code} onChange={(e) => setLocForm({ ...locForm, code: e.target.value })} /></Col>
            <Col md={2}><Form.Check label="Head office" checked={locForm.isHeadOffice} onChange={(e) => setLocForm({ ...locForm, isHeadOffice: e.target.checked })} /></Col>
            <Col md={2}><Button size="sm" disabled={saving} onClick={saveLocation}>Add location</Button></Col>
          </Row>
          <h3 className="company-onboarding__card-title">Departments</h3>
          <Table size="sm"><thead><tr><th>Name</th><th>Code</th></tr></thead>
            <tbody>{departments.map((d) => <tr key={d._id}><td>{d.name}</td><td>{d.code}</td></tr>)}</tbody>
          </Table>
          <Row className="g-2">
            <Col md={4}><Form.Control placeholder="Name" value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} /></Col>
            <Col md={3}><Form.Control placeholder="Code" value={deptForm.code} onChange={(e) => setDeptForm({ ...deptForm, code: e.target.value })} /></Col>
            <Col md={2}><Button size="sm" disabled={saving} onClick={saveDepartment}>Add department</Button></Col>
          </Row>
        </div>
      )}

      {step === 3 && (
        <div className="company-onboarding__card">
          <Alert variant="info" className="small">
            The platform owner receives the Platform Administrator role. They manage Workspace settings; at least one active platform administrator must remain.
          </Alert>
          <Form.Select isInvalid={!!fieldErrors.owner} value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)}>
            <option value="">Select user…</option>
            {eligibleUsers.map((u) => (
              <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
            ))}
          </Form.Select>
        </div>
      )}

      {step === 4 && (
        <div className="company-onboarding__card">
          <Row className="g-3">
            <Col md={4}><Form.Label>Currency</Form.Label><Form.Control value={regional.currency} onChange={(e) => setRegional({ ...regional, currency: e.target.value })} /></Col>
            <Col md={4}><Form.Label>Timezone</Form.Label><Form.Control value={regional.timezone} onChange={(e) => setRegional({ ...regional, timezone: e.target.value })} /></Col>
            <Col md={4}><Form.Label>FY start month</Form.Label><Form.Control type="number" min={1} max={12} value={regional.financialYearStartMonth} onChange={(e) => setRegional({ ...regional, financialYearStartMonth: Number(e.target.value) })} /></Col>
          </Row>
        </div>
      )}

      {step === 5 && (
        <div className="company-onboarding__card">
          <Form.Check
            type="switch"
            id="tender-app"
            label={`Enable ${TENDER_APP_NAME} for this organisation`}
            checked={tenderEnabled}
            onChange={(e) => setTenderEnabled(e.target.checked)}
          />
        </div>
      )}

      {step === 6 && (
        <div className="company-onboarding__card small">
          <Alert variant={data?.company?.onboardingStatus === 'active' ? 'success' : 'secondary'}>
            Status: {data?.company?.onboardingStatus || 'draft'}
          </Alert>
          <ul className="mb-0">
            <li><strong>{org.legalName}</strong> ({org.code}) — {org.metadata?.legalEntityType?.replace(/_/g, ' ')}</li>
            <li>CIN: {cp.cin || '—'} · ISIN: {cp.isin || '—'}</li>
            <li>Listed: {cp.listedOnBse ? 'BSE' : ''}{cp.listedOnBse && cp.listedOnNse ? ', ' : ''}{cp.listedOnNse ? 'NSE' : ''}{!cp.listedOnBse && !cp.listedOnNse ? '—' : ''}</li>
            <li>{org.contact?.address?.city || '—'}, {org.contact?.address?.state || '—'}</li>
            <li>{locations.length} location(s), {departments.length} department(s)</li>
            <li>Owner: {eligibleUsers.find((u) => u._id === ownerUserId)?.email || '—'}</li>
            <li>{regional.currency} · {regional.timezone}</li>
            <li>{TENDER_APP_NAME}: {tenderEnabled ? 'enabled' : 'disabled'}</li>
          </ul>
        </div>
      )}

      <div className="company-onboarding__actions">
        {step > 1 ? <Button variant="outline-secondary" disabled={saving} onClick={() => setStep(step - 1)}>Back</Button> : null}
        {step < 6 ? (
          <Button variant="primary" disabled={saving} onClick={() => save(step + 1)}>{saving ? 'Saving…' : 'Save & continue'}</Button>
        ) : (
          <Button variant="primary" disabled={saving || data?.company?.onboardingStatus === 'active'} onClick={activate}>
            {saving ? 'Activating…' : 'Activate organisation'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CompanyOnboardingPage;
